import { makeStyles } from "@fluentui/react-components";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import { designTokens } from "theme/customTheme";

export const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    backgroundColor: projectIqTokens.bg,
    fontFamily: projectIqTokens.fontFamily,
  },

  body: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    padding: designTokens.spacing[8],
  },

  content: {
    maxWidth: "1120px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[4],
  },

  card: {
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.rule}`,
    borderRadius: "14px",
    boxShadow: projectIqTokens.shadowSm,
  },

  hero: {
    padding: `${designTokens.spacing[6]} 26px ${designTokens.spacing[4]} 26px`,
  },

  heroBody: {
    display: "flex",
    gap: designTokens.spacing[1],
  },

  heroMain: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },

  heroMainHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },

  heroSide: {
    flexShrink: 0,
    width: "256px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: designTokens.spacing[6],
    borderLeft: `1px solid ${projectIqTokens.rule}`,
    paddingLeft: "28px",
  },

  heroSideNoProgress: {
    justifyContent: "flex-end",
    paddingBottom: designTokens.spacing[3],
  },

  progressBlock: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "6px",
    width: "100%",
  },

  progressLabelRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    width: "100%",
    gap: designTokens.spacing[4],
  },

  progressLabel: {
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.muted,
  },

  progressPercent: {
    fontSize: "12.5px",
    fontWeight: designTokens.fontWeight.bold,
    color: projectIqTokens.ink2,
  },

  progressTrack: {
    width: "100%",
    height: "6px",
    borderRadius: designTokens.borderRadius.full,
    backgroundColor: projectIqTokens.bg2,
    overflow: "hidden",
  },

  progressFill: {
    height: "100%",
    borderRadius: designTokens.borderRadius.full,
    backgroundColor: `var(--hero-progress-fill, ${projectIqTokens.teal})`,
  },

  heroTitle: {
    fontSize: "16px",
    fontWeight: 800,
    color: projectIqTokens.ink,
    letterSpacing: "-.01em",
    lineHeight: designTokens.lineHeight.tight,
    marginTop: "6px",
  },

  clientNameInline: {
    fontWeight: designTokens.fontWeight.normal,
    color: projectIqTokens.muted,
  },

  portfolioPracticeRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[4],
    fontSize: "14.5px",
    lineHeight: 1.55,
    color: projectIqTokens.ink3,
    marginTop: designTokens.spacing[1],
  },

  description: {
    fontSize: "14.5px",
    lineHeight: 1.55,
    color: projectIqTokens.ink3,
    maxWidth: "640px",
    marginTop: designTokens.spacing[4],
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    cursor: "default",
  },

  members: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexShrink: 0,
  },

  avatarGroup: {
    display: "flex",
    alignItems: "center",
  },

  avatar: {
    boxShadow: `0 0 0 2px ${projectIqTokens.card}`,
    ":not(:first-child)": {
      marginLeft: "-8px",
    },
  },

  avatarMore: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "30px",
    height: "30px",
    borderRadius: designTokens.borderRadius.full,
    backgroundColor: projectIqTokens.bg2,
    color: projectIqTokens.ink3,
    fontSize: "11.5px",
    fontWeight: designTokens.fontWeight.bold,
    marginLeft: "-8px",
    boxShadow: `0 0 0 2px ${projectIqTokens.card}`,
  },

  membersCount: {
    fontSize: "12.5px",
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.muted,
  },

  sectionTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "18px 0 0 2px",
  },

  cardTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  sectionTitle: {
    fontSize: "15px",
    fontWeight: designTokens.fontWeight.bold,
    color: projectIqTokens.ink,
    paddingBottom: designTokens.spacing[3],
  },

  comingSoonTag: {
    fontSize: "10px",
    fontWeight: designTokens.fontWeight.bold,
    letterSpacing: ".06em",
    textTransform: "uppercase",
    color: projectIqTokens.faint,
    backgroundColor: projectIqTokens.bg2,
    border: `1px solid ${projectIqTokens.rule}`,
    borderRadius: designTokens.borderRadius.sm,
    padding: "2px 7px",
  },

  opportunityCard: {
    padding: "18px 22px 22px",
  },

  metricGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: designTokens.spacing[5],
    marginTop: designTokens.spacing[4],
  },

  metric: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
  },

  metricLabel: {
    fontSize: "12.5px",
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.muted,
  },

  metricValue: {
    fontSize: "26px",
    fontWeight: 800,
    color: projectIqTokens.ink,
    letterSpacing: "-.01em",
  },

  metricHint: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.faint,
  },

  statusGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: designTokens.spacing[4],
  },

  statusTile: {
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.rule}`,
    borderRadius: "14px",
    boxShadow: projectIqTokens.shadowSm,
    padding: `${designTokens.spacing[4]} 18px`,
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
  },

  statusLabel: {
    fontSize: "12.5px",
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.muted,
  },

  statusValueRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
  },

  statusDot: {
    width: "8px",
    height: "8px",
    borderRadius: designTokens.borderRadius.full,
    flexShrink: 0,
  },

  statusValue: {
    fontSize: "15px",
    fontWeight: designTokens.fontWeight.bold,
    color: projectIqTokens.ink,
  },

  twoCol: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: designTokens.spacing[4],
  },

  listCard: {
    padding: "18px 20px 20px",
    display: "flex",
    flexDirection: "column",
    minWidth: "0",
  },

  milestoneRow: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    padding: "7px 0",
    borderTop: `1px solid ${projectIqTokens.rule}`,
    ":first-child": {
      borderTop: "none",
    },
  },

  milestoneTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: designTokens.spacing[2],
  },

  milestoneTitleLine: {
    display: "flex",
    alignItems: "baseline",
    gap: "4px",
    flex: "1 1 0%",
    minWidth: "0",
    overflow: "hidden",
  },

  milestoneInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    flex: "1 1 0%",
    minWidth: "0",
  },

  milestoneName: {
    fontSize: "13.5px",
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.ink2,
    minWidth: "0",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  milestoneDue: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.faint,
  },

  raidRow: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    padding: "7px 0",
    borderTop: `1px solid ${projectIqTokens.rule}`,
    ":first-child": {
      borderTop: "none",
    },
  },

  raidTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: designTokens.spacing[2],
  },

  raidPillsRow: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    flexShrink: 0,
  },

  raidType: {
    fontSize: designTokens.fontSize.xxs,
    fontWeight: designTokens.fontWeight.bold,
    letterSpacing: ".05em",
    color: projectIqTokens.muted,
  },

  raidText: {
    fontSize: "13.5px",
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.ink2,
    flex: "1 1 0%",
    minWidth: "0",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  itemDescription: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.faint,
    flex: "1 1 0%",
    minWidth: "0",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  raidBodyCol: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: "0",
  },

  raidMeta: {
    fontSize: designTokens.fontSize.xxs,
    color: projectIqTokens.faint,
  },

  raidPills: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    alignItems: "flex-end",
  },

  pill: {
    fontSize: designTokens.fontSize.xxs,
    fontWeight: designTokens.fontWeight.bold,
    borderRadius: designTokens.borderRadius.full,
    padding: "3px 10px",
    whiteSpace: "nowrap",
  },
  pillComplete: {
    color: projectIqTokens.green,
    backgroundColor: "rgba(47,158,110,0.12)",
  },
  pillProgress: {
    color: projectIqTokens.blue,
    backgroundColor: "rgba(63,143,194,0.12)",
  },
  pillUpcoming: {
    color: projectIqTokens.muted,
    backgroundColor: projectIqTokens.bg2,
  },
  pillHigh: {
    color: projectIqTokens.rust,
    backgroundColor: "rgba(194,85,63,0.12)",
  },
  pillMedium: {
    color: projectIqTokens.amber,
    backgroundColor: "rgba(217,138,43,0.12)",
  },
  pillLow: {
    color: projectIqTokens.muted,
    backgroundColor: projectIqTokens.bg2,
  },

  cardHeaderRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    columnGap: designTokens.spacing[3],
    rowGap: designTokens.spacing[2],
    paddingBottom: designTokens.spacing[3],
    borderBottom: `1px solid ${projectIqTokens.rule}`,
    marginBottom: designTokens.spacing[3],
  },

  cardTitle: {
    fontSize: "15px",
    fontWeight: designTokens.fontWeight.bold,
    color: projectIqTokens.ink,
    flexShrink: 0,
  },

  counterBar: {
    display: "flex",
    flexWrap: "wrap",
    gap: designTokens.spacing[2],
    paddingBottom: designTokens.spacing[3],
    borderBottom: `1px solid ${projectIqTokens.rule}`,
    marginBottom: designTokens.spacing[3],
  },

  counterBarNoSep: {
    display: "flex",
    flexWrap: "wrap",
    gap: designTokens.spacing[2],
    marginBottom: designTokens.spacing[2],
  },

  counterChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
    padding: `${designTokens.spacing[1]} ${designTokens.spacing[3]}`,
    borderRadius: designTokens.borderRadius.full,
    fontSize: designTokens.fontSize.xxs,
    fontWeight: designTokens.fontWeight.semibold,
    lineHeight: "1.4",
    userSelect: "none",
  },

  counterChipClickable: {
    cursor: "pointer",
  },

  counterChipActive: {
    boxShadow: "0 0 0 2px currentColor",
  },

  scrollableList: {
    flex: 1,
    minHeight: "0",
    maxHeight: "320px",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: "0",
  },

  viewPlanLink: {
    display: "block",
    textAlign: "left",
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.muted,
    cursor: "pointer",
    marginTop: "auto",
    paddingTop: designTokens.spacing[3],
    background: "none",
    border: "none",
    padding: "0",
    fontFamily: "inherit",
    ":hover": {
      color: projectIqTokens.teal,
      textDecoration: "underline",
    },
    ":focus-visible": {
      outline: `2px solid ${projectIqTokens.teal}`,
      outlineOffset: "2px",
      borderRadius: "2px",
    },
  },

  subSectionTitle: {
    display: "block",
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.muted,
    marginBottom: designTokens.spacing[2],
  },

  raidSummarySection: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[1],
    marginBottom: designTokens.spacing[2],
  },

  emptyHint: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.muted,
    textAlign: "center",
    padding: designTokens.spacing[4],
    margin: "0",
  },

  raidTypeRisk: {
    backgroundColor: "rgba(194,85,63,0.10)",
    color: projectIqTokens.rust,
  },
  raidTypeAction: {
    backgroundColor: "rgba(14,154,167,0.10)",
    color: projectIqTokens.teal,
  },
  raidTypeIssue: {
    backgroundColor: "rgba(217,138,43,0.10)",
    color: projectIqTokens.amber,
  },
  raidTypeDecision: {
    backgroundColor: "rgba(107,106,217,0.10)",
    color: projectIqTokens.violet,
  },

  pillAtRisk: {
    backgroundColor: "rgba(217,138,43,0.12)",
    color: projectIqTokens.amber,
  },
});
